import { ButtonProps, Chip, SmartusComponentsModule } from '@affinis/smartus-components';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MtxGridColumn, MtxGridModule } from '@ng-matero/extensions/grid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SortDirection, SortService } from '@shared/services/sort.service';
import { LocalStorageService } from '@shared/services/storage.service';
import { FilterDialogComponent, IFilterDialogData } from '../filter-dialog/filter-dialog.component';
import { DEFAULT_TABLE_CONFIG, ITableConfig } from './table-base.types';

@Component({
  selector: 'app-table-base',
  templateUrl: './table-base.html',
  styleUrls: ['./table-base.scss'],
  imports: [MtxGridModule, SmartusComponentsModule, FilterDialogComponent, TranslateModule],
})
export class TableBaseComponent implements OnChanges {
  // Table props
  @Input() config: ITableConfig = DEFAULT_TABLE_CONFIG;
  @Input() columns: Array<MtxGridColumn> = [];
  @Input() data: unknown[] = [];
  filteredData: unknown[] = [];

  @Input() isLoading = false;

  @Input() componentIdentifier = 'default';

  @Input() translationPath = '';

  @Input() additionalControlButtons: ButtonProps[] = [];
  baseControlButtons: ButtonProps[] = [];

  @Output() public readonly rowClick = new EventEmitter();

  @Output() public readonly changedPage = new EventEmitter<PageEvent>();

  @Output() readonly refreshData = new EventEmitter();

  @Output() public readonly rowSelectionChange = new EventEmitter<any>();

  @Output() readonly filterChanged = new EventEmitter<any>();

  // Filter Props
  chips: Chip[] = [];

  modalComponent = FilterDialogComponent;

  dialogData: IFilterDialogData = {
    filterFields: this.config.grid.filter.filterFields ?? [],
    filterValues: this.config.grid.filter.filterOptions,
    actions: {
      onSubmit: (formModel: any) => {
        this.mapFormModelToChips(formModel);
        this.updateFilter(formModel);
        this.filterData();
      },
      onReset: () => {
        this.mapFormModelToChips({});
        this.updateFilter({});
        this.filterData();
      },
    },
  };

  constructor(
    private translateService: TranslateService,
    private storageService: LocalStorageService
  ) {
    this.baseControlButtons = [
      {
        label: this.translateService.instant('table.refresh'),
        icon: 'rotate',
        onClick: () => {
          this.refresh();
        },
      },
    ];
  }
  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        if (propName === 'data') {
          this.filterData();
        } else if (propName === 'config') {
          this.dialogData.filterFields = this.config.grid.filter.filterFields ?? [];
          this.dialogData.filterValues = this.config.grid.filter.filterOptions;
          this.mapFormModelToChips(this.config.grid.filter.filterOptions);
        }
      }
    }
  }

  refresh() {
    this.refreshData.emit();
  }

  get controlButtons(): ButtonProps[] {
    return [...this.additionalControlButtons, ...this.baseControlButtons];
  }

  filterData() {
    if (!this.config.grid.filter.filterOnFront) {
      return;
    }

    this.filteredData = structuredClone(this.data);
    const filterOptions = this.config.grid.filter.filterOptions;

    for (const key of Object.keys(filterOptions)) {
      const filterValue = filterOptions[key];

      if (filterValue == null || filterValue === '' || filterValue.length === 0) {
        continue;
      }

      this.filteredData = this.filteredData.filter((x: any) => {
        const data = x[key];

        if (Array.isArray(filterValue)) {
          return this.filterArray(data, filterValue);
        }

        if (typeof data === 'string') {
          return this.filterString(data, filterValue);
        }

        return data == filterValue;
      });
    }
  }

  private filterArray(data: any, filterValue: any[]): boolean {
    if (!Array.isArray(data)) {
      return filterValue.includes(data);
    }

    return data.some(y => filterValue.includes(y));
  }

  private filterString(data: string, filterValue: string): boolean {
    return data.toLowerCase().includes(filterValue.toLowerCase());
  }

  handleOnRemoveChip = (eventObj: any) => {
    delete this.config.grid.filter.filterOptions[eventObj.id];
    this.filterChanged.emit(this.config.grid.filter.filterOptions);
    this.chips = this.chips.filter(chipItem => chipItem.id !== eventObj.id);
    this.filterData();
  };

  updateFilter(filterOptions: any) {
    this.config.grid.filter.filterOptions = filterOptions;
    this.dialogData.filterValues = this.config.grid.filter.filterOptions;
    this.filterChanged.emit(this.config.grid.filter.filterOptions);
  }

  sortChange(sort: Sort) {
    if (this.config.grid.filter.sortOnFront) {
      if (sort.direction === '') {
        this.filterData();
        return;
      }

      this.filteredData = [
        ...SortService.sort(this.filteredData, sort.direction as SortDirection, sort.active),
      ];
    }
  }

  changePage(e: PageEvent) {
    this.config.grid.paging.pageIndex = e.pageIndex;
    this.config.grid.paging.pageSize = e.pageSize;
    this.changedPage.emit(e);
  }

  /**
   * Retrieves the translated value for a given translation key.
   * If no translation exists for the specified key, returns the provided fallback value.
   *
   * @param {string} translationKey - The key for the desired translation.
   * @param {any} fallbackValue - The value to return if no translation exists for the given key.
   * @returns {string} - The translated value or the fallback value.
   */
  private getTranslatedValueOrFallback(translationKey: string, fallbackValue: any): string {
    const translatedValue = this.translateService.instant(translationKey);
    return translatedValue !== translationKey ? translatedValue : fallbackValue;
  }

  private mapFormModelToChips(formModel: object) {
    const tmpChips: Chip[] = [];
    Object.keys(formModel).forEach(formModelItemKey => {
      const field = this.config.grid.filter.filterFields?.find(fieldItem => {
        return fieldItem.key === formModelItemKey;
      });
      if (field) {
        if (formModelItemKey === 'createDateTo' || formModelItemKey === 'createDateFrom') {
          const date = new Date(formModel[formModelItemKey]).toLocaleDateString('de-DE');
          tmpChips.push({
            id: formModelItemKey,
            label: `${field.props?.label}: ${date}`,
          });
        } else if (formModel[formModelItemKey]) {
          const valueToDisplay = this.getTranslatedValueOrFallback(
            `${this.translationPath}.${formModel[formModelItemKey]}`,
            formModel[formModelItemKey]
          );

          tmpChips.push({
            id: formModelItemKey,
            label: `${field.props?.label}: ${valueToDisplay}`,
          });
        }
      }
    });
    this.chips = tmpChips;
  }

  saveColumnsConfiguration(componentIdentifier: string) {
    const allSettings = this.storageService.getObject(componentIdentifier) || {};
    allSettings.ColumnsConfig = this.columns;
    this.storageService.set(componentIdentifier, allSettings);
  }

  // remember visibility state of columns for refresh
  saveColumnVisibility() {
    this.columns.forEach(x => {
      x.hide = !x.show;
    });
    this.saveColumnsConfiguration(this.componentIdentifier);
  }
}
